import { MontantCalculDirective } from './directives/montant-calcul.directive';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, HostListener, OnInit, ViewChild, computed, signal } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ComptesService } from './services/comptes.service';
import { AuthService } from './services/auth.service';
import { Compte, MiseAJourCompte } from './models/compte.model';
import { OperationPlanifiee, TypeOperation } from './models/operation-planifiee.model';
import { OperationReelle } from './models/operation-reelle.model';
import { PrevisionMensuelle } from './models/prevision.model';
import { Categorie } from './models/categorie.model';
import { firstValueFrom } from 'rxjs';
import { filtrerOperationsReellesParMois, totalOperationsReelles as totalOpsReelles } from './utils/operations.utils';
import { PlanAnnuelComponent } from './components/plan-annuel/plan-annuel.component';
import { PopupPointagePlanifieeComponent, PointageConfirmPayload, AjoutOperationProvisionPayload } from './components/shared/popup-pointage-planifiee/popup-pointage-planifiee.component';
import { PopupEditionPlanifieeComponent, EditionPlanifieePayload } from './components/shared/popup-edition-planifiee/popup-edition-planifiee.component';
import { APP_VERSION } from './app';

const EURO = '\u20ac';

@Component({
    selector: 'app-root',
    imports: [CommonModule, ReactiveFormsModule, FormsModule, MontantCalculDirective, PlanAnnuelComponent, PopupPointagePlanifieeComponent, PopupEditionPlanifieeComponent],
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent implements OnInit {
  titre = 'Kompta';
  readonly appVersion = APP_VERSION;
  readonly dateDuJour = new Date();
  currentUserName: string = '';

  @ViewChild('pointerMontantInput') pointerMontantInput?: ElementRef<HTMLInputElement>;
  @ViewChild('operationPlanifieeLibelleInput') operationPlanifieeLibelleInput?: ElementRef<HTMLInputElement>;
  @ViewChild('operationPlanifieeMontantInput') operationPlanifieeMontantInput?: ElementRef<HTMLInputElement>;
  @ViewChild('operationReelleMontantInput') operationReelleMontantInput?: ElementRef<HTMLInputElement>;

  readonly aujourdHui = new Date();
  readonly periode = signal<{ annee: number; mois: number }>({
    annee: this.aujourdHui.getFullYear(),
    mois: this.aujourdHui.getMonth() + 1,
  });

  readonly compteSelectionne = signal<Compte | null>(null);
  readonly categories = signal<Categorie[]>([]);
  readonly moyensPaiement = [
    { label: 'Prélèvement', value: 'PRELEVEMENT' },
    { label: 'CB', value: 'CB' },
    { label: 'CB différé', value: 'CB_DIFFERE' },
    { label: 'Chèque', value: 'CHEQUE' },
    { label: 'Virement', value: 'VIREMENT' },
  ] as const;
  readonly operationsPlanifiees = signal<OperationPlanifiee[]>([]);
  readonly operationsReelles = signal<OperationReelle[]>([]);
  readonly operationsReellesHistorique = signal<Record<number, OperationReelle[]>>({});
  readonly prevision = signal<PrevisionMensuelle | null>(null);
  readonly operationPlanifieeEditee = signal<OperationPlanifiee | null>(null);

  // Tri et filtres pour opérations planifiées
  readonly triPlanifieeColonne = signal<'libelle' | 'montant' | 'jour' | 'categorie' | null>(null);
  readonly triPlanifieeOrdre = signal<'asc' | 'desc'>('asc');

  // Filtres par colonne avec dropdowns contextuels
  readonly filtreLibelleTexte = signal<string>('');
  readonly filtreMontantMin = signal<number | null>(null);
  readonly filtreMontantMax = signal<number | null>(null);
  readonly filtreMontantType = signal<'TOUS' | 'ENTREE' | 'SORTIE'>('TOUS');
  readonly filtreJour = signal<number | null>(null);
  readonly filtreCategorieTexte = signal<string>('');
  readonly filtreCategorieIds = signal<number[]>([]);
  readonly filtreProvision = signal<'TOUS' | 'OUI' | 'NON'>('TOUS');

  // État des dropdowns (quelle colonne est ouverte)
  readonly colonneDropdownOuverte = signal<'libelle' | 'montant' | 'jour' | 'categorie' | null>(null);

  // Computed pour le nombre de filtres actifs par colonne
  readonly nombreFiltresLibelle = computed(() => this.filtreLibelleTexte() ? 1 : 0);
  readonly nombreFiltresMontant = computed(() => {
    let count = 0;
    if (this.filtreMontantMin() !== null) count++;
    if (this.filtreMontantMax() !== null) count++;
    if (this.filtreMontantType() !== 'TOUS') count++;
    return count;
  });
  readonly nombreFiltresJour = computed(() => this.filtreJour() !== null ? 1 : 0);
  readonly nombreFiltresCategorie = computed(() => {
    let count = 0;
    if (this.filtreCategorieTexte()) count++;
    if (this.filtreCategorieIds().length > 0) count++;
    return count;
  });

  readonly operationsPlanifieesTrieesEtFiltrees = computed(() => {
    let operations: (OperationPlanifiee & { estReelle?: boolean })[] = [
      ...this.operationsPlanifiees(),
    ];

    // Injecter les opérations réelles non pointées
    const operationsReelles = this.operationsReelles();
    const reellesNonPointees = operationsReelles
      .filter((op) => !op.estPointee)
      .map((op) => ({
        ...op,
        // Mapping vers OperationPlanifiee
        id: op.id, // Attention ID potentiellement en conflit, utiliser estReelle pour distinguer
        dateDebut: op.dateOperation,
        jourDuMois: new Date(op.dateOperation).getDate(),
        estEtalee: false,
        provision: false,
        valeurRestante: null,
        nombreMensualites: null,
        montantInitial: null,
        compteDestinationId: op.compteDestinationId,
        estReelle: true,
      } as unknown as OperationPlanifiee & { estReelle?: boolean }));

    operations = [...operations, ...reellesNonPointees];

    // Filtre Libellé
    const texteLibelle = this.filtreLibelleTexte().toLowerCase();
    if (texteLibelle) {
      operations = operations.filter(op =>
        op.libelle.toLowerCase().includes(texteLibelle)
      );
    }

    // Filtre Montant
    if (this.filtreMontantMin() !== null) {
      operations = operations.filter(op => Math.abs(op.montant) >= this.filtreMontantMin()!);
    }
    if (this.filtreMontantMax() !== null) {
      operations = operations.filter(op => Math.abs(op.montant) <= this.filtreMontantMax()!);
    }
    if (this.filtreMontantType() !== 'TOUS') {
      operations = operations.filter(op => op.type === this.filtreMontantType());
    }

    // Filtre Jour
    if (this.filtreJour() !== null) {
      operations = operations.filter(op => op.jourDuMois === this.filtreJour());
    }

    // Filtre Catégorie
    const texteCategorie = this.filtreCategorieTexte().toLowerCase();
    if (texteCategorie) {
      operations = operations.filter(op =>
        (op.categorie ?? '').toLowerCase().includes(texteCategorie)
      );
    }
    // Note: filtreCategorieIds non utilisé car categorie est une string dans le modèle

    // Filtre Provision (global) - On exclut les opérations réelles de ce filtre s'il est actif sur OUI/NON car elles ont 'provision: false' par défaut
    // Ou on considère qu'elles ne sont pas des provisions.
    if (this.filtreProvision() !== 'TOUS') {
      const estProvision = this.filtreProvision() === 'OUI';
      operations = operations.filter(op => op.provision === estProvision);
    }

    // Appliquer le tri
    const colonne = this.triPlanifieeColonne();
    if (colonne) {
      operations.sort((a, b) => {
        // Provisions always on top
        if (a.provision && !b.provision) return -1;
        if (!a.provision && b.provision) return 1;

        let valA: any;
        let valB: any;

        switch (colonne) {
          case 'libelle':
            valA = a.libelle.toLowerCase();
            valB = b.libelle.toLowerCase();
            break;
          case 'montant':
            valA = a.montant;
            valB = b.montant;
            break;
          case 'jour':
            valA = a.jourDuMois;
            valB = b.jourDuMois;
            break;
          case 'categorie':
            valA = (a.categorie ?? '').toLowerCase();
            valB = (b.categorie ?? '').toLowerCase();
            break;
        }

        if (valA < valB) return this.triPlanifieeOrdre() === 'asc' ? -1 : 1;
        if (valA > valB) return this.triPlanifieeOrdre() === 'asc' ? 1 : -1;
        return 0;
      });
    } else {
      // Default sort by day but provisions on top
      operations.sort((a, b) => {
        if (a.provision && !b.provision) return -1;
        if (!a.provision && b.provision) return 1;
        return a.jourDuMois - b.jourDuMois;
      });
    }

    return operations;
  });

  // Tri et filtres pour opérations réelles
  readonly triReelleColonne = signal<'libelle' | 'montant' | 'date' | 'statut' | null>(null);
  readonly triReelleOrdre = signal<'asc' | 'desc'>('asc');

  // Filtres par colonne pour opérations réelles
  readonly filtreReelleLibelleTexte = signal<string>('');
  readonly filtreReelleMontantMin = signal<number | null>(null);
  readonly filtreReelleMontantMax = signal<number | null>(null);
  readonly filtreReelleMontantType = signal<'TOUS' | 'ENTREE' | 'SORTIE'>('TOUS');
  readonly filtreReelleDate = signal<string>('');
  readonly filtreReelleStatut = signal<'TOUS' | 'POINTEE' | 'A_POINTER'>('TOUS');

  // État des dropdowns pour opérations réelles
  readonly colonneReelleDropdownOuverte = signal<'libelle' | 'montant' | 'date' | 'statut' | null>(null);

  // Computed pour le nombre de filtres actifs par colonne (réelles)
  readonly nombreFiltresReelleLibelle = computed(() => this.filtreReelleLibelleTexte() ? 1 : 0);
  readonly nombreFiltresReelleMontant = computed(() => {
    let count = 0;
    if (this.filtreReelleMontantMin() !== null) count++;
    if (this.filtreReelleMontantMax() !== null) count++;
    if (this.filtreReelleMontantType() !== 'TOUS') count++;
    return count;
  });
  readonly nombreFiltresReelleDate = computed(() => this.filtreReelleDate() ? 1 : 0);
  readonly nombreFiltresReelleStatut = computed(() => this.filtreReelleStatut() !== 'TOUS' ? 1 : 0);
  readonly nombreOperationsReellesNonPointees = computed(() => this.operationsReelles().filter(op => !op.estPointee).length);

  readonly operationsReellesTrieesEtFiltrees = computed(() => {
    // Par défaut, on ne montre que les opérations pointées dans cette liste,
    // car les non-pointées sont injectées dans la liste des opérations planifiées.
    let operations = this.operationsReelles().filter(op => op.estPointee);

    // Filtre Libellé
    const texteLibelle = this.filtreReelleLibelleTexte().toLowerCase();
    if (texteLibelle) {
      operations = operations.filter(op =>
        op.libelle.toLowerCase().includes(texteLibelle)
      );
    }

    // Filtre Montant
    if (this.filtreReelleMontantMin() !== null) {
      operations = operations.filter(op => Math.abs(op.montant) >= this.filtreReelleMontantMin()!);
    }
    if (this.filtreReelleMontantMax() !== null) {
      operations = operations.filter(op => Math.abs(op.montant) <= this.filtreReelleMontantMax()!);
    }
    if (this.filtreReelleMontantType() !== 'TOUS') {
      operations = operations.filter(op => op.type === this.filtreReelleMontantType());
    }

    // Filtre Date
    if (this.filtreReelleDate()) {
      const dateRecherche = this.filtreReelleDate();
      operations = operations.filter(op =>
        op.dateOperation.includes(dateRecherche)
      );
    }

    // Filtre Statut
    if (this.filtreReelleStatut() === 'POINTEE') {
      operations = operations.filter(op => op.estPointee === true);
    } else if (this.filtreReelleStatut() === 'A_POINTER') {
      operations = operations.filter(op => op.estPointee === false);
    }

    // Appliquer le tri
    const colonne = this.triReelleColonne();
    if (colonne) {
      operations.sort((a, b) => {
        let valA: any;
        let valB: any;

        switch (colonne) {
          case 'libelle':
            valA = a.libelle.toLowerCase();
            valB = b.libelle.toLowerCase();
            break;
          case 'montant':
            valA = a.montant;
            valB = b.montant;
            break;
          case 'date':
            valA = new Date(a.dateOperation).getTime();
            valB = new Date(b.dateOperation).getTime();
            break;
          case 'statut':
            valA = a.estPointee ? 1 : 0;
            valB = b.estPointee ? 1 : 0;
            break;
        }

        if (valA < valB) return this.triReelleOrdre() === 'asc' ? -1 : 1;
        if (valA > valB) return this.triReelleOrdre() === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return operations;
  });

  readonly operationReelleEditee = signal<OperationReelle | null>(null);
  readonly operationReelleFormOuverte = signal(false);
  readonly operationPlanifieeFormOuverte = signal(false);
  readonly operationPlanifieeAPointer = signal<OperationPlanifiee | null>(null);
  readonly montantPointagePropose = signal<number>(0);
  readonly enregistrementProvision = signal(false);

  readonly chargementPlanifiees = signal(false);
  readonly chargementReelles = signal(false);
  readonly chargementPrevision = signal(false);
  readonly editeSolde = signal(false);

  readonly erreurs = signal<string | null>(null);
  readonly succes = signal<string | null>(null);

  readonly comptes = computed(() => this.comptesService.comptes());

  readonly compteFormOuverte = signal(false);
  readonly compteEditee = signal<Compte | null>(null);
  readonly afficherComptes = signal(false);
  readonly vueActive = signal<'detail' | 'budget'>('budget');
  readonly modeTelephone = signal(
    typeof window !== 'undefined' && window.matchMedia('(max-width: 600px)').matches
  );

  @HostListener('window:resize')
  adapterAffichageTelephone(): void {
    this.modeTelephone.set(window.matchMedia('(max-width: 600px)').matches);
  }

  libelleCompteMobile(nom: string): string {
    return nom.replace(/^Compte\s+/i, '');
  }

  selectionnerCompteParId(compteId: number | null): void {
    const compte = this.comptes().find((c) => c.id === compteId);
    if (compte && compte.id !== this.compteSelectionne()?.id) {
      this.selectionnerCompte(compte);
    }
  }
  readonly planAnnuelRefreshToken = signal(0);

  basculerAffichageComptes(): void {
    this.afficherComptes.update((v) => !v);
  }

  activerVue(vue: 'detail' | 'budget'): void {
    this.vueActive.set(vue);
  }

  readonly compteForm = this.fb.nonNullable.group({
    nom: ['', Validators.required],
    soldeInitial: [0],
    soldeCBDiffere: [0],
  });

  readonly operationPlanifieeForm = this.fb.group({
    libelle: ['', Validators.required],
    type: ['SORTIE', Validators.required],
    montant: [0, Validators.required],
    dateDebut: ['', Validators.required],
    nombreDeMois: [null],
    jourDuMois: [1, [Validators.required, Validators.min(1), Validators.max(31)]],
    estEtalee: [false],
    moyenPaiement: [null as string | null],
    numeroCheque: [''],
    compteDestinationId: [null as number | null],
    provision: [false],
    valeurRestante: [0, [Validators.min(0)]],
    nombreMensualites: [null as number | null],
    montantInitial: [0, [Validators.min(0.01)]],
    categorie: [''],
  });
  readonly etalementActif = signal(false);

  readonly operationReelleForm = this.fb.group({
    libelle: ['', Validators.required],
    type: ['SORTIE', Validators.required],
    montant: [0, Validators.required],
    dateOperation: ['', Validators.required],
    dateValeur: [''],
    estPointee: [false],
    operationPlanifieeId: [null as number | null],
    categorie: [''],
    moyenPaiement: [null as string | null],
    numeroCheque: [''],
    compteDestinationId: [null as number | null],
  });

  readonly pointerOperationPlanifieeForm = this.fb.group({
    montant: [0, [Validators.required, Validators.min(0.01)]],
    moyenPaiement: [null as string | null],
    numeroCheque: [{ value: '', disabled: true }],
    compteDestinationId: [{ value: null as number | null, disabled: true }],
  });

  readonly soldeActuelForm = this.fb.nonNullable.group({
    soldeActuel: [0, Validators.required],
  });
  readonly soldeCBDiffereForm = this.fb.nonNullable.group({
    soldeCBDiffere: [0, Validators.required],
  });

  private readonly soldesPlanifiesMemo = new Map<string, number>();
  private soldeBaseCourant = 0;
  private periodeMemoMax: { annee: number; mois: number } | null = null;

  constructor(
    private readonly fb: FormBuilder,
    public readonly comptesService: ComptesService,
    private readonly authService: AuthService,
    private readonly router: Router
  ) {
    this.currentUserName = this.authService.getDisplayName();

    this.soldeActuelForm.disable();
    this.soldeCBDiffereForm.disable();
    this.pointerOperationPlanifieeForm.disable();
    this.pointerOperationPlanifieeForm.get('moyenPaiement')?.valueChanges.subscribe((valeur) => {
      this.mettreAJourChampsMoyenPaiementPointage(valeur);
    });
    this.mettreAJourChampsMoyenPaiementPointage(
      this.pointerOperationPlanifieeForm.get('moyenPaiement')?.value
    );

    this.operationPlanifieeForm.get('provision')?.valueChanges.subscribe((provisionActive) => {
      const valeurCtrl = this.operationPlanifieeForm.get('valeurRestante');
      if (!valeurCtrl) {
        return;
      }

      if (provisionActive) {
        const montant = Number(this.operationPlanifieeForm.get('montant')?.value ?? 0);
        const edition = this.operationPlanifieeEditee();
        if (!edition || edition.provision === false) {
          valeurCtrl.setValue(Number.isFinite(montant) ? montant : 0);
        }
      } else {
        valeurCtrl.setValue(0);
      }
    });

    this.operationPlanifieeForm.get('montant')?.valueChanges.subscribe((montant) => {
      const provisionActive = this.operationPlanifieeForm.get('provision')?.value;
      const valeurCtrl = this.operationPlanifieeForm.get('valeurRestante');
      if (!provisionActive || !valeurCtrl || this.operationPlanifieeEditee()) {
        return;
      }

      const valeur = Number(montant ?? 0);
      valeurCtrl.setValue(Number.isFinite(valeur) ? valeur : 0);
    });

    this.operationPlanifieeForm.get('moyenPaiement')?.valueChanges.subscribe((valeur) => {
      this.mettreAJourChampsMoyenPaiementPlanifie(valeur);
    });
    this.mettreAJourChampsMoyenPaiementPlanifie(this.operationPlanifieeForm.get('moyenPaiement')?.value);

    this.operationPlanifieeForm.get('estEtalee')?.valueChanges.subscribe((etat) => {
      this.mettreAJourChampsEtalement(etat);
    });
    this.mettreAJourChampsEtalement(this.operationPlanifieeForm.get('estEtalee')?.value);
    this.operationPlanifieeForm.get('montantInitial')?.valueChanges.subscribe(() => {
      this.mettreAJourMontantMensualite();
    });
    this.operationPlanifieeForm.get('nombreMensualites')?.valueChanges.subscribe(() => {
      this.mettreAJourMontantMensualite();
    });

    this.operationReelleForm.get('moyenPaiement')?.valueChanges.subscribe((valeur) => {
      this.mettreAJourChampsMoyenPaiementReelle(valeur);
    });
    this.mettreAJourChampsMoyenPaiementReelle(this.operationReelleForm.get('moyenPaiement')?.value);
  }

  ngOnInit(): void {
    void this.comptesService.rafraichirComptes().subscribe({
      next: (comptes) => {
        if (comptes.length > 0) {
          this.selectionnerCompte(comptes[0]);
        }
      },
      error: () => this.signalerErreur('Impossible de recuperer les comptes.'),
    });
    this.chargerCategories();
  }

  goToAdmin(): void {
    this.router.navigate(['/admin']);
  }

  logout(): void {
    this.authService.logout();
    this.router.navigate(['/login']);
  }

  selectionnerCompte(compte: Compte): void {
    this.compteSelectionne.set(compte);
    this.reinitialiserFormOperationPlanifiee();
    this.operationReelleEditee.set(null);
    this.operationReelleFormOuverte.set(false);
    this.annulerPointageOperationPlanifiee();
    this.mettreAJourSoldeActuelForm(compte);
    this.mettreAJourSoldeCBDiffereForm(compte);
    this.soldesPlanifiesMemo.clear();
    this.soldeBaseCourant = 0;
    this.periodeMemoMax = null;
    void this.chargerDonneesCompte(compte.id);
  }

  periodePrecedente(): void {
    const { annee, mois } = this.periode();
    const nouvelleDate = new Date(annee, mois - 2, 1);
    this.periode.set({
      annee: nouvelleDate.getFullYear(),
      mois: nouvelleDate.getMonth() + 1,
    });
    this.rechargerPeriodeCourante();
  }

  periodeSuivante(): void {
    const { annee, mois } = this.periode();
    const nouvelleDate = new Date(annee, mois, 1);
    this.periode.set({
      annee: nouvelleDate.getFullYear(),
      mois: nouvelleDate.getMonth() + 1,
    });
    this.rechargerPeriodeCourante();
  }

  periodeCourante(): void {
    this.periode.set({
      annee: this.aujourdHui.getFullYear(),
      mois: this.aujourdHui.getMonth() + 1,
    });
    this.rechargerPeriodeCourante();
  }

  creerCompte(): void {
    if (this.compteForm.invalid) {
      this.compteForm.markAllAsTouched();
      return;
    }

    const valeur = this.compteForm.getRawValue();
    const nomSaisi = (valeur.nom ?? '').trim();
    if (!nomSaisi) {
      this.compteForm.controls.nom.setValue('');
      this.compteForm.controls.nom.setErrors({ required: true });
      return;
    }

    const soldeInitial =
      valeur.soldeInitial !== null && valeur.soldeInitial !== undefined
        ? Number(valeur.soldeInitial)
        : undefined;
    const soldeCBDiffere =
      valeur.soldeCBDiffere !== null && valeur.soldeCBDiffere !== undefined
        ? Number(valeur.soldeCBDiffere)
        : undefined;

    const edition = this.compteEditee();

    if (edition) {
      const payload: MiseAJourCompte = {};
      if (nomSaisi !== edition.nom) {
        payload.nom = nomSaisi;
      }
      if (soldeInitial !== undefined && !Number.isNaN(soldeInitial)) {
        payload.soldeActuel = soldeInitial;
      }
      if (soldeCBDiffere !== undefined && !Number.isNaN(soldeCBDiffere)) {
        payload.soldeCBDiffere = soldeCBDiffere;
      }

      if (Object.keys(payload).length === 0) {
        this.reinitialiserFormCompte();
        this.afficherSucces('Aucune modification à enregistrer.');
        return;
      }

      void this.comptesService.mettreAJourCompte(edition.id, payload).subscribe({
        next: (compteMisAJour) => {
          this.afficherSucces(`Compte "${compteMisAJour.nom}" mis à jour.`);
          this.reinitialiserFormCompte();
          this.compteSelectionne.set(compteMisAJour);
        },
        error: () => this.signalerErreur("Impossible de mettre à jour le compte."),
      });
      return;
    }

    void this.comptesService
      .creerCompte({ nom: nomSaisi, soldeInitial, soldeCBDiffere })
      .subscribe({
        next: (compte) => {
          this.afficherSucces(`Compte "${compte.nom}" cree.`);
          this.reinitialiserFormCompte();
        },
        error: () => this.signalerErreur('Impossible de creer le compte.'),
      });
  }

  ouvrirCreationCompte(): void {
    this.compteEditee.set(null);
    this.compteForm.reset({ nom: '', soldeInitial: 0, soldeCBDiffere: 0 });
    this.compteForm.markAsPristine();
    this.compteFormOuverte.set(true);
  }

  editerCompte(compte: Compte): void {
    this.compteEditee.set(compte);
    // Pre-fill with current visible balance (soldeActuel if present)
    const solde = compte.soldeActuel !== undefined && compte.soldeActuel !== null ? compte.soldeActuel : compte.soldeInitial;
    this.compteForm.reset({
      nom: compte.nom,
      soldeInitial: solde as any,
      soldeCBDiffere: compte.soldeCBDiffere ?? 0,
    });
    this.compteForm.markAsPristine();
    this.compteFormOuverte.set(true);
  }

  reinitialiserFormCompte(): void {
    this.compteForm.reset({ nom: '', soldeInitial: 0, soldeCBDiffere: 0 });
    this.compteEditee.set(null);
    this.compteFormOuverte.set(false);
  }

  annulerEditionCompte(): void {
    this.reinitialiserFormCompte();
  }

  async chargerDonneesCompte(compteId: number): Promise<void> {
    const periode = this.periode();

    await Promise.all([
      this.chargerOperationsPlanifiees(compteId, periode.annee, periode.mois),
      this.chargerOperationsReelles(compteId, periode.annee, periode.mois),
    ]);
    await this.chargerPrevision(compteId, periode.annee, periode.mois);
  }

  async chargerOperationsPlanifiees(
    compteId: number,
    annee: number,
    mois: number
  ): Promise<void> {
    this.chargementPlanifiees.set(true);
    return new Promise((resolve) => {
      this.comptesService
        .listerOperationsPlanifiees(compteId, annee, mois)
        .subscribe({
          next: async (operations) => {
            this.operationsPlanifiees.set(operations);
            const historique: Record<number, OperationReelle[]> = {};
            operations.forEach((op) => {
              if (op.operationsReelles && op.operationsReelles.length) {
                historique[op.id] = op.operationsReelles;
              }
            });
            this.operationsReellesHistorique.set(historique);
            this.chargementPlanifiees.set(false);
            resolve();
          },
          error: () => {
            this.signalerErreur('Impossible de charger les operations planifiees.');
            this.chargementPlanifiees.set(false);
            resolve();
          },
        });
    });
  }

  async chargerOperationsReelles(
    compteId: number,
    annee: number,
    mois: number
  ): Promise<void> {
    this.chargementReelles.set(true);
    return new Promise((resolve) => {
      this.comptesService.listerOperationsReelles(compteId, annee, mois).subscribe({
        next: (operations) => {
          this.operationsReelles.set(operations);
          this.chargementReelles.set(false);
          resolve();
        },
        error: () => {
          this.signalerErreur('Impossible de charger les operations reelles.');
          this.chargementReelles.set(false);
          resolve();
        },
      });
    });
  }

  async chargerPrevision(
    compteId: number,
    annee: number,
    mois: number
  ): Promise<void> {
    this.chargementPrevision.set(true);
    return new Promise((resolve) => {
      this.comptesService.chargerPrevision(compteId, annee, mois).subscribe({
        next: (prevision) => {
          const compteActuel = this.compteSelectionne();
          const soldeBase = this.calculerSoldeBasePourPeriode(
            annee,
            mois,
            compteActuel,
            Number(prevision.compte.soldeInitial)
          );
          this.soldeBaseCourant = soldeBase;

          const soldeReelCalcule = Number(
            (soldeBase + prevision.reel.entrees - prevision.reel.sorties).toFixed(2)
          );


          const netRestantPlanifie = this.totalRestantPlanifie(prevision);
          const netRestantReelle = this.totalRestantReelle(prevision);

          const periode = this.periode();
          const aujourdHui = this.aujourdHui;
          const estPeriodeCourante =
            periode.annee === aujourdHui.getFullYear() &&
            periode.mois === aujourdHui.getMonth() + 1;

          const soldeCBDiffere = compteActuel && compteActuel.soldeCBDiffere !== undefined && compteActuel.soldeCBDiffere !== null
            ? Number(compteActuel.soldeCBDiffere)
            : 0;

          let soldeBaseAjuste = Number((soldeBase + soldeCBDiffere).toFixed(2));
          if (!estPeriodeCourante) {
            soldeBaseAjuste = soldeBase;
          }



          const soldePlanifieCalcule = Number((soldeBaseAjuste + netRestantPlanifie + netRestantReelle).toFixed(2));

          const previsionAjustee: PrevisionMensuelle = {
            ...prevision,
            planifie: {
              ...prevision.planifie,
              solde: soldePlanifieCalcule,
            },
            reel: {
              ...prevision.reel,
              solde: soldeReelCalcule,
            },
          };

          const periodeCourante = { annee, mois };
          const cleCourante = this.clePeriode(annee, mois);
          const doitMemoriser =
            this.periodeMemoMax === null ||
            this.estPeriodeUlterieureOuEgale(periodeCourante, this.periodeMemoMax) ||
            this.soldesPlanifiesMemo.has(cleCourante);

          if (doitMemoriser) {
            this.soldesPlanifiesMemo.set(cleCourante, soldePlanifieCalcule);

            if (
              this.periodeMemoMax === null ||
              this.estPeriodeUlterieureOuEgale(periodeCourante, this.periodeMemoMax)
            ) {
              this.periodeMemoMax = periodeCourante;
            }
          }

          this.prevision.set(previsionAjustee);
          this.chargementPrevision.set(false);
          resolve();
        },
        error: () => {
          this.signalerErreur('Impossible de calculer la prevision.');
          this.chargementPrevision.set(false);
          this.soldeBaseCourant = 0;
          resolve();
        },
      });
    });
  }

  async soumettreOperationPlanifiee(): Promise<void> {
    const compte = this.compteSelectionne();
    if (this.operationPlanifieeForm.invalid || !compte) {
      this.operationPlanifieeForm.markAllAsTouched();
      return;
    }

    const periode = this.periode();
    const valeur = this.operationPlanifieeForm.getRawValue();
    const typePlanifie = (valeur.type ?? 'SORTIE') as TypeOperation;
    const provisionActive = valeur.provision === true;
    const estEtaleeActive = valeur.estEtalee === true;
    const nombreMensualites =
      estEtaleeActive && valeur.nombreMensualites !== null && valeur.nombreMensualites !== undefined
        ? Number(valeur.nombreMensualites)
        : null;
    const montantInitial =
      estEtaleeActive && valeur.montantInitial !== null && valeur.montantInitial !== undefined
        ? Number(valeur.montantInitial)
        : null;
    const valeurRestanteBrute =
      valeur.valeurRestante !== null && valeur.valeurRestante !== undefined
        ? Number(valeur.valeurRestante)
        : Number(valeur.montant ?? 0);
    const valeurRestanteNormalisee = provisionActive
      ? Number.isFinite(valeurRestanteBrute)
        ? Math.max(0, valeurRestanteBrute)
        : Number(valeur.montant ?? 0)
      : undefined;

    const moyenPaiement = valeur.moyenPaiement ?? null;
    const numeroChequeBrut = valeur.numeroCheque ?? '';
    const numeroCheque =
      moyenPaiement === 'CHEQUE' && numeroChequeBrut
        ? numeroChequeBrut.trim()
        : undefined;
    const compteDestinationId =
      moyenPaiement === 'VIREMENT' &&
        valeur.compteDestinationId !== null &&
        valeur.compteDestinationId !== undefined
        ? Number(valeur.compteDestinationId)
        : undefined;
    const categorieChoisie =
      valeur.categorie && valeur.categorie.trim().length > 0
        ? valeur.categorie.trim()
        : undefined;

    const payload = {
      libelle: valeur.libelle ?? '',
      type: typePlanifie,
      montant: Number(valeur.montant ?? 0),
      dateDebut: valeur.dateDebut ?? '',
      nombreDeMois:
        valeur.nombreDeMois !== null && valeur.nombreDeMois !== undefined && valeur.nombreDeMois !== ''
          ? Number(valeur.nombreDeMois)
          : null,
      jourDuMois: Number(valeur.jourDuMois ?? 1),
      estEtalee: estEtaleeActive,
      provision: provisionActive,
      valeurRestante: provisionActive
        ? Number((valeurRestanteNormalisee ?? 0).toFixed(2))
        : undefined,
      nombreMensualites:
        estEtaleeActive && nombreMensualites ? nombreMensualites : undefined,
      montantInitial:
        estEtaleeActive && montantInitial ? Number(montantInitial.toFixed(2)) : undefined,
      moyenPaiement: moyenPaiement ?? undefined,
      numeroCheque,
      compteDestinationId,
      categorie: categorieChoisie,
    };

    const edition = this.operationPlanifieeEditee();

    if (edition) {
      void this.comptesService
        .mettreAJourOperationPlanifiee(edition.id, payload)
        .subscribe({
          next: () => {
            this.afficherSucces('Operation planifiee mise a jour.');
            this.reinitialiserFormOperationPlanifiee();
            void this.chargerOperationsPlanifiees(compte.id, periode.annee, periode.mois);
            void this.chargerPrevision(compte.id, periode.annee, periode.mois);
          },
          error: () => this.signalerErreur("Impossible de mettre a jour l'operation planifiee."),
        });
      return;
    }

    if (
      !edition &&
      estEtaleeActive &&
      nombreMensualites !== null &&
      nombreMensualites > 1 &&
      montantInitial !== null &&
      montantInitial > 0
    ) {
      try {
        await this.creerOperationsMensualisees(compte, valeur, payload, {
          nombreMensualites,
          montantInitial,
          dateDebut: valeur.dateDebut ?? '',
        });
        this.reinitialiserFormOperationPlanifiee();
        await this.chargerOperationsPlanifiees(compte.id, periode.annee, periode.mois);
        await this.chargerPrevision(compte.id, periode.annee, periode.mois);
        this.afficherSucces('Operations planifiees ajoutees.');
      } catch (error) {
        this.signalerErreur("Impossible d'ajouter les operations planifiees.");
      }
      return;
    }

    void this.comptesService
      .creerOperationPlanifiee(compte.id, payload)
      .subscribe({
        next: () => {
          this.reinitialiserFormOperationPlanifiee();
          void this.chargerOperationsPlanifiees(compte.id, periode.annee, periode.mois);
          void this.chargerPrevision(compte.id, periode.annee, periode.mois);
          this.planAnnuelRefreshToken.update((v) => v + 1);
          this.afficherSucces('Operation planifiee ajoutee.');
        },
        error: () => this.signalerErreur("Impossible d'ajouter l'operation planifiee."),
      });
  }

  editerOperationPlanifiee(operation: OperationPlanifiee): void {
    this.operationPlanifieeEditee.set(operation);
    this.operationPlanifieeForm.patchValue({
      libelle: operation.libelle,
      type: operation.type,
      montant: operation.montant,
      dateDebut: operation.dateDebut,
      nombreDeMois: (operation.nombreDeMois ?? null) as any,
      jourDuMois: operation.jourDuMois,
      estEtalee: operation.estEtalee,
      provision: operation.provision,
      valeurRestante: operation.provision
        ? operation.valeurRestante ?? operation.montant
        : 0,
      moyenPaiement: operation.moyenPaiement ?? null,
      numeroCheque: operation.numeroCheque ?? '',
      compteDestinationId: operation.compteDestinationId ?? null,
      nombreMensualites: operation.nombreMensualites ?? null,
      montantInitial: operation.montantInitial ?? operation.montant,
      categorie: operation.categorie ?? '',
    });
    this.operationPlanifieeForm.markAsPristine();
    this.operationPlanifieeFormOuverte.set(true);
    this.mettreAJourChampsMoyenPaiementPlanifie(operation.moyenPaiement ?? null);
    this.mettreAJourChampsEtalement(operation.estEtalee);
    setTimeout(() => {
      const input = this.operationPlanifieeMontantInput?.nativeElement;
      input?.focus();
      input?.select();
    }, 0);
  }

  annulerEditionOperationPlanifiee(): void {
    this.reinitialiserFormOperationPlanifiee();
  }

  ouvrirCreationOperationPlanifiee(): void {
    this.operationPlanifieeEditee.set(null);
    const aujourdHui = new Date();
    const isoDate = aujourdHui.toISOString().slice(0, 10);
    this.operationPlanifieeForm.reset({
      libelle: '',
      type: 'SORTIE',
      montant: 0,
      dateDebut: isoDate,
      nombreDeMois: null,
      jourDuMois: 1,
      estEtalee: false,
      moyenPaiement: null,
      numeroCheque: '',
      compteDestinationId: null,
      provision: false,
      valeurRestante: 0,
      nombreMensualites: null,
      montantInitial: 0,
      categorie: '',
    });
    this.operationPlanifieeForm.markAsPristine();
    this.operationPlanifieeFormOuverte.set(true);
    this.mettreAJourChampsMoyenPaiementPlanifie(null);
    this.mettreAJourChampsEtalement(false);
    setTimeout(() => {
      const input = this.operationPlanifieeLibelleInput?.nativeElement;
      input?.focus();
      input?.select();
    }, 0);
  }

  fermerCanvasOperationPlanifiee(): void {
    this.annulerEditionOperationPlanifiee();
  }

  preparerPointageOperationPlanifiee(operation: OperationPlanifiee): void {
    const compte = this.compteSelectionne();
    if (!compte) {
      return;
    }

    const periode = this.periode();
    const provisionActive = operation.provision === true;

    const montantPlanifie = this.montantPlanifiePourOperation(
      operation,
      periode.annee,
      periode.mois
    );
    const montantNormalise = provisionActive
      ? Number(Math.max(0, montantPlanifie).toFixed(2))
      : Number(montantPlanifie.toFixed(2));

    if (montantNormalise <= 0 && !provisionActive) {
      this.signalerErreur("Cette operation n'est pas planifiee pour cette periode.");
      return;
    }

    const moyenPaiementDefaut = operation.moyenPaiement ?? null;
    const numeroChequeDefaut =
      operation.moyenPaiement === 'CHEQUE' ? operation.numeroCheque ?? '' : '';
    const compteDestinationDefaut =
      operation.moyenPaiement === 'VIREMENT' && operation.compteDestinationId
        ? operation.compteDestinationId
        : null;

    this.pointerOperationPlanifieeForm.enable();
    this.pointerOperationPlanifieeForm.reset({
      montant: montantNormalise,
      moyenPaiement: moyenPaiementDefaut,
      numeroCheque: numeroChequeDefaut,
      compteDestinationId: compteDestinationDefaut,
    });
    this.mettreAJourChampsMoyenPaiementPointage(moyenPaiementDefaut);
    this.pointerOperationPlanifieeForm.markAsPristine();
    this.montantPointagePropose.set(montantNormalise);
    this.erreurs.set(null);
    this.operationPlanifieeAPointer.set(operation);
    setTimeout(() => this.pointerMontantInput?.nativeElement.select(), 0);
  }

  confirmerPointageOperationPlanifiee(continuer: boolean = false): void {
    const operation = this.operationPlanifieeAPointer();
    const compte = this.compteSelectionne();

    if (!operation || !compte) {
      return;
    }

    if (this.pointerOperationPlanifieeForm.invalid) {
      this.pointerOperationPlanifieeForm.markAllAsTouched();
      return;
    }

    const periode = this.periode();
    const valeur = this.pointerOperationPlanifieeForm.getRawValue();
    const montant = Number(valeur.montant);
    const payload: {
      montant: number;
      dateOperation?: string;
      moyenPaiement?: string | null;
      numeroCheque?: string;
      compteDestinationId?: number | null;
    } = { montant };

    if (valeur.moyenPaiement !== undefined) {
      payload.moyenPaiement = valeur.moyenPaiement;
    }

    if (
      valeur.moyenPaiement === 'CHEQUE' &&
      typeof valeur.numeroCheque === 'string' &&
      valeur.numeroCheque.trim().length > 0
    ) {
      payload.numeroCheque = valeur.numeroCheque.trim();
    }

    if (
      valeur.moyenPaiement === 'VIREMENT' &&
      valeur.compteDestinationId !== null &&
      valeur.compteDestinationId !== undefined
    ) {
      payload.compteDestinationId = valeur.compteDestinationId;
    }

    void this.comptesService
      .pointerOperationPlanifiee(operation.id, periode.annee, periode.mois, payload)
      .subscribe({
        next: (operationReelleCreee) => {
          if (operation.provision && operationReelleCreee) {
            const historique = this.operationsReellesHistorique();
            const existantes = historique[operation.id] ?? [];
            historique[operation.id] = [
              ...existantes,
              operationReelleCreee,
            ];
            this.operationsReellesHistorique.set({ ...historique });
          }

          if (continuer) {
            // Reset modal for next entry but keep it open
            this.pointerOperationPlanifieeForm.patchValue({
              montant: 0,
              // Keep payment method as is or reset? Usually continuous entry implies similar method, but let's reset amount at least.
              // keeping moyenPaiement from previous entry might be useful. Let's keep it.
            });
            setTimeout(() => {
              const input = this.pointerMontantInput?.nativeElement;
              input?.focus();
              input?.select();
            }, 0);
          } else {
            this.annulerPointageOperationPlanifiee();
          }

          void this.rechargerPeriodeCourante();
          this.actualiserCompteSelectionne();
          this.afficherSucces(
            operation.provision ? 'Provision utilisee.' : 'Operation planifiee pointee.'
          );
        },
        error: () =>
          this.signalerErreur("Impossible de pointer l'operation planifiee."),
      });
  }

  annulerPointageOperationPlanifiee(): void {
    this.operationPlanifieeAPointer.set(null);
    this.pointerOperationPlanifieeForm.reset({
      montant: 0,
      moyenPaiement: null,
      numeroCheque: '',
      compteDestinationId: null,
    });
    this.mettreAJourChampsMoyenPaiementPointage(null);
    this.pointerOperationPlanifieeForm.disable();
    this.montantPointagePropose.set(0);
  }

  // ── Handlers composants partagés ────────────────────────────────────────

  onConfirmerPointagePlanifiee(payload: PointageConfirmPayload): void {
    const operation = this.operationPlanifieeAPointer();
    const compte = this.compteSelectionne();
    if (!operation || !compte) return;

    const periode = this.periode();
    const servicePayload: {
      montant: number;
      moyenPaiement?: string | null;
      numeroCheque?: string;
      compteDestinationId?: number | null;
    } = { montant: payload.montant };

    if (payload.moyenPaiement !== undefined) {
      servicePayload.moyenPaiement = payload.moyenPaiement;
    }
    if (payload.numeroCheque) {
      servicePayload.numeroCheque = payload.numeroCheque;
    }
    if (payload.compteDestinationId !== null && payload.compteDestinationId !== undefined) {
      servicePayload.compteDestinationId = payload.compteDestinationId;
    }

    void this.comptesService
      .pointerOperationPlanifiee(operation.id, periode.annee, periode.mois, servicePayload)
      .subscribe({
        next: (operationReelleCreee) => {
          if (operation.provision && operationReelleCreee) {
            const historique = this.operationsReellesHistorique();
            const existantes = historique[operation.id] ?? [];
            historique[operation.id] = [...existantes, operationReelleCreee];
            this.operationsReellesHistorique.set({ ...historique });
          }
          if (!payload.continuer) {
            this.annulerPointageOperationPlanifiee();
          }
          void this.rechargerPeriodeCourante();
          this.actualiserCompteSelectionne();
          this.afficherSucces(
            operation.provision ? 'Provision utilisee.' : 'Operation planifiee pointee.'
          );
        },
        error: () => this.signalerErreur("Impossible de pointer l'operation planifiee."),
      });
  }

  async onAjouterOperationProvision(payload: AjoutOperationProvisionPayload): Promise<void> {
    const operation = this.operationPlanifieeAPointer();
    const compte = this.compteSelectionne();
    if (!operation?.provision || !compte || this.enregistrementProvision()) return;
    this.enregistrementProvision.set(true);
    this.erreurs.set(null);
    try {
      const reelle = await firstValueFrom(this.comptesService.creerOperationReelle(compte.id, {
        ...payload,
        type: operation.type,
        operationPlanifieeId: operation.id,
        categorie: operation.categorie ?? undefined,
        estPointee: false,
      }));
      const historique = this.operationsReellesHistorique();
      this.operationsReellesHistorique.set({
        ...historique, [operation.id]: [...(historique[operation.id] ?? []), reelle],
      });
      await this.rafraichirProvisionEnPointage();
    } catch {
      this.erreurs.set("Impossible d'ajouter l'opération non pointée à la provision.");
    } finally {
      this.enregistrementProvision.set(false);
    }
  }

  async onPointerOperationProvision(reelleId: number): Promise<void> {
    const operation = this.operationPlanifieeAPointer();
    if (!operation?.provision || this.enregistrementProvision() ||
      !this.operationsNonPointeesPourProvision(operation.id).some((op) => op.id === reelleId)) return;
    await this.changerPointageOperationProvision(reelleId, true);
  }

  async onDepointerOperationProvision(reelleId: number): Promise<void> {
    const operation = this.operationPlanifieeAPointer();
    if (!operation?.provision || this.enregistrementProvision() ||
      !this.operationsReellesPourOperation(operation.id).some((op) => op.id === reelleId)) return;
    await this.changerPointageOperationProvision(reelleId, false);
  }

  private async changerPointageOperationProvision(reelleId: number, estPointee: boolean): Promise<void> {
    this.enregistrementProvision.set(true);
    this.erreurs.set(null);
    try {
      await firstValueFrom(estPointee
        ? this.comptesService.pointerOperationReelle(reelleId)
        : this.comptesService.depointerOperationReelle(reelleId));
      await this.rafraichirProvisionEnPointage();
    } catch {
      this.erreurs.set("Impossible de modifier le pointage de l'opération liée à la provision.");
    } finally {
      this.enregistrementProvision.set(false);
    }
  }

  async onReporterOperationProvision(reelleId: number): Promise<void> {
    const operation = this.operationPlanifieeAPointer();
    if (!operation?.provision || this.enregistrementProvision()) return;
    const reelle = this.operationsNonPointeesPourProvision(operation.id).find((op) => op.id === reelleId);
    if (!reelle) return;
    const { annee, mois } = this.periode();
    const prochainMois = new Date(annee, mois, 1);
    const dernierJour = new Date(annee, mois + 1, 0).getDate();
    const jour = Math.min(Number(reelle.dateOperation.substring(8, 10)), dernierJour);
    const dateOperation = `${prochainMois.getFullYear()}-${String(prochainMois.getMonth() + 1).padStart(2, '0')}-${String(jour).padStart(2, '0')}`;
    this.enregistrementProvision.set(true);
    this.erreurs.set(null);
    try {
      await firstValueFrom(this.comptesService.mettreAJourOperationReelle(reelleId, { dateOperation }));
      const historique = this.operationsReellesHistorique();
      this.operationsReellesHistorique.set({
        ...historique, [operation.id]: (historique[operation.id] ?? []).map((op) =>
          op.id === reelleId ? { ...op, dateOperation } : op),
      });
      await this.rafraichirProvisionEnPointage();
    } catch {
      this.erreurs.set("Impossible de reporter l'opération au mois suivant.");
    } finally {
      this.enregistrementProvision.set(false);
    }
  }

  async onSupprimerOperationProvision(reelleId: number): Promise<void> {
    const operation = this.operationPlanifieeAPointer();
    if (!operation?.provision || this.enregistrementProvision() ||
      !this.operationsNonPointeesPourProvision(operation.id).some((op) => op.id === reelleId)) return;
    this.enregistrementProvision.set(true);
    this.erreurs.set(null);
    try {
      await firstValueFrom(this.comptesService.supprimerOperationReelle(reelleId));
      const historique = this.operationsReellesHistorique();
      this.operationsReellesHistorique.set({
        ...historique, [operation.id]: (historique[operation.id] ?? []).filter((op) => op.id !== reelleId),
      });
      await this.rafraichirProvisionEnPointage();
    } catch {
      this.erreurs.set("Impossible de supprimer l'opération non pointée liée à la provision.");
    } finally {
      this.enregistrementProvision.set(false);
    }
  }

  private async rafraichirProvisionEnPointage(): Promise<void> {
    const compte = this.compteSelectionne();
    if (!compte) return;
    await this.chargerDonneesCompte(compte.id);
    const operation = this.operationPlanifieeAPointer();
    const fraiche = this.operationsPlanifiees().find((op) => op.id === operation?.id);
    if (fraiche) {
      this.operationPlanifieeAPointer.set(fraiche);
      const periode = this.periode();
      this.montantPointagePropose.set(this.montantPlanifiePourOperation(fraiche, periode.annee, periode.mois));
    } else if (operation?.provision) {
      // Une provision hors période sans opération en attente sort de la liste mensuelle.
      this.operationPlanifieeAPointer.set({ ...operation, valeurRestante: 0 });
      this.montantPointagePropose.set(0);
    }
    this.actualiserCompteSelectionne();
  }

  async onSauvegarderPlanifiee(payload: EditionPlanifieePayload): Promise<void> {
    const compte = this.compteSelectionne();
    if (!compte) return;

    const periode = this.periode();
    const edition = this.operationPlanifieeEditee();

    const servicePayload = {
      libelle: payload.libelle,
      type: payload.type as TypeOperation,
      montant: payload.montant,
      dateDebut: payload.dateDebut,
      jourDuMois: payload.jourDuMois,
      nombreDeMois: payload.nombreDeMois ?? null,
      estEtalee: payload.estEtalee,
      provision: payload.provision,
      valeurRestante: payload.provision
        ? (payload.valeurRestante !== null ? Number(payload.valeurRestante.toFixed(2)) : undefined)
        : undefined,
      nombreMensualites:
        payload.estEtalee && payload.nombreMensualites ? payload.nombreMensualites : undefined,
      montantInitial:
        payload.estEtalee && payload.montantInitial
          ? Number(payload.montantInitial.toFixed(2))
          : undefined,
      moyenPaiement: payload.moyenPaiement ?? null,
      numeroCheque: payload.numeroCheque || undefined,
      compteDestinationId: payload.compteDestinationId ?? undefined,
      compteDestinationExterne: payload.compteDestinationExterne ?? undefined,
      virementProgramme: payload.virementProgramme,
      categorie: payload.categorie || undefined,
      periodicite: payload.periodicite,
    };

    if (edition) {
      void this.comptesService.mettreAJourOperationPlanifiee(edition.id, servicePayload).subscribe({
        next: () => {
          this.afficherSucces('Operation planifiee mise a jour.');
          this.reinitialiserFormOperationPlanifiee();
          void this.chargerOperationsPlanifiees(compte.id, periode.annee, periode.mois);
          void this.chargerPrevision(compte.id, periode.annee, periode.mois);
        },
        error: () => this.signalerErreur("Impossible de mettre a jour l'operation planifiee."),
      });
      return;
    }

    if (
      payload.estEtalee &&
      payload.nombreMensualites !== null &&
      payload.nombreMensualites! > 1 &&
      payload.montantInitial !== null &&
      payload.montantInitial! > 0
    ) {
      try {
        await this.creerOperationsMensualisees(compte, { libelle: payload.libelle }, servicePayload, {
          nombreMensualites: payload.nombreMensualites!,
          montantInitial: payload.montantInitial!,
          dateDebut: payload.dateDebut,
        });
        this.reinitialiserFormOperationPlanifiee();
        await this.chargerOperationsPlanifiees(compte.id, periode.annee, periode.mois);
        await this.chargerPrevision(compte.id, periode.annee, periode.mois);
        this.planAnnuelRefreshToken.update((v) => v + 1);
        this.afficherSucces('Operations planifiees ajoutees.');
      } catch {
        this.signalerErreur("Impossible d'ajouter les operations planifiees.");
      }
      return;
    }

    void this.comptesService.creerOperationPlanifiee(compte.id, servicePayload).subscribe({
      next: () => {
        this.reinitialiserFormOperationPlanifiee();
        void this.chargerOperationsPlanifiees(compte.id, periode.annee, periode.mois);
        void this.chargerPrevision(compte.id, periode.annee, periode.mois);
        this.planAnnuelRefreshToken.update((v) => v + 1);
        this.afficherSucces('Operation planifiee ajoutee.');
      },
      error: () => this.signalerErreur("Impossible d'ajouter l'operation planifiee."),
    });
  }

  pointerOperationPlanifieeDirect(operation: OperationPlanifiee): void {
    const compte = this.compteSelectionne();
    if (!compte || operation.provision) {
      return;
    }

    const periode = this.periode();
    const montant = this.montantPlanifiePourOperation(operation, periode.annee, periode.mois);

    if (montant <= 0) {
      this.signalerErreur("Cette operation n'est pas planifiee pour cette periode.");
      return;
    }

    void this.comptesService
      .pointerOperationPlanifiee(operation.id, periode.annee, periode.mois, {
        montant: Number(montant.toFixed(2)),
      })
      .subscribe({
        next: () => {
          void this.rechargerPeriodeCourante();
          this.actualiserCompteSelectionne();
          this.afficherSucces('Operation planifiee pointee.');
        },
        error: () => this.signalerErreur("Impossible de pointer l'operation planifiee."),
      });
  }

  operationsReellesPourOperation(operationId: number): OperationReelle[] {
    const operations = this.operationsReellesHistorique()[operationId] ?? [];
    const { annee, mois } = this.periode();
    return filtrerOperationsReellesParMois(operations, annee, mois).filter((op) => op.estPointee);
  }

  operationsNonPointeesPourProvision(operationId: number): OperationReelle[] {
    const { annee, mois } = this.periode();
    const moisAffiche = `${annee}-${String(mois).padStart(2, '0')}`;
    return (this.operationsReellesHistorique()[operationId] ?? [])
      .filter((op) => !op.estPointee && op.dateOperation.substring(0, 7) <= moisAffiche)
      .sort((a, b) => b.dateOperation.localeCompare(a.dateOperation));
  }

  totalOperationsReelles(operationId: number): number {
    return totalOpsReelles(this.operationsReellesPourOperation(operationId));
  }

  supprimerOperationPlanifiee(operationId: number): void {
    const compte = this.compteSelectionne();
    if (!compte) {
      return;
    }

    const confirmation = window.confirm('Supprimer définitivement cette opération planifiée ?');
    if (!confirmation) {
      return;
    }

    const periode = this.periode();

    void this.comptesService.supprimerOperationPlanifiee(operationId).subscribe({
      next: () => {
        void this.chargerOperationsPlanifiees(compte.id, periode.annee, periode.mois);
        this.afficherSucces('Operation planifiee supprimee.');
      },
      error: () => this.signalerErreur('Suppression impossible.'),
    });
  }

  ouvrirCreationOperationReelle(): void {
    this.operationReelleEditee.set(null);
    const aujourdHui = new Date();
    const isoDate = aujourdHui.toISOString().slice(0, 10);

    this.operationReelleForm.reset({
      libelle: '',
      type: 'SORTIE',
      montant: 0,
      dateOperation: isoDate,
      dateValeur: '',
      estPointee: false,
      operationPlanifieeId: null,
      categorie: '',
      moyenPaiement: null,
      numeroCheque: '',
      compteDestinationId: null,
    });
    this.operationReelleForm.markAsPristine();
    this.operationReelleFormOuverte.set(true);
    this.mettreAJourChampsMoyenPaiementReelle(null);
  }

  editerOperationReelle(operation: OperationReelle): void {
    this.operationReelleEditee.set(operation);

    const planifieeIdBrut = operation.operationPlanifieeId;
    const planifieeIdNumerique =
      planifieeIdBrut !== null && planifieeIdBrut !== undefined
        ? Number(planifieeIdBrut)
        : null;

    this.operationReelleForm.patchValue({
      libelle: operation.libelle,
      type: operation.type,
      montant: operation.montant,
      dateOperation: operation.dateOperation ?? '',
      dateValeur: operation.dateValeur ?? '',
      estPointee: operation.estPointee,
      operationPlanifieeId:
        planifieeIdNumerique !== null && !Number.isNaN(planifieeIdNumerique)
          ? planifieeIdNumerique
          : null,
      categorie: operation.categorie ?? '',
      moyenPaiement: operation.moyenPaiement ?? null,
      numeroCheque: operation.numeroCheque ?? '',
      compteDestinationId: operation.compteDestinationId ?? null,
    });
    this.operationReelleForm.markAsPristine();
    this.operationReelleFormOuverte.set(true);
    this.mettreAJourChampsMoyenPaiementReelle(operation.moyenPaiement ?? null);
    setTimeout(() => {
      const input = this.operationReelleMontantInput?.nativeElement;
      input?.focus();
      input?.select();
    }, 0);
  }

  annulerEditionOperationReelle(): void {
    this.operationReelleForm.reset({
      libelle: '',
      type: 'SORTIE',
      montant: 0,
      dateOperation: '',
      dateValeur: '',
      estPointee: false,
      operationPlanifieeId: null,
      categorie: '',
      moyenPaiement: null,
      numeroCheque: '',
      compteDestinationId: null,
    });
    this.operationReelleEditee.set(null);
    this.operationReelleFormOuverte.set(false);
    this.mettreAJourChampsMoyenPaiementReelle(null);
  }

  fermerCanvasOperationReelle(): void {
    this.annulerEditionOperationReelle();
  }

  soumettreOperationReelle(): void {
    const compte = this.compteSelectionne();
    if (this.operationReelleForm.invalid || !compte) {
      this.operationReelleForm.markAllAsTouched();
      return;
    }

    const periode = this.periode();
    const valeur = this.operationReelleForm.getRawValue();
    const typeReel = (valeur.type ?? 'SORTIE') as TypeOperation;
    const moyenPaiement = valeur.moyenPaiement ?? null;
    const numeroChequeBrut = valeur.numeroCheque ?? '';
    const numeroCheque =
      moyenPaiement === 'CHEQUE' && numeroChequeBrut
        ? numeroChequeBrut.trim()
        : undefined;
    const compteDestinationId =
      moyenPaiement === 'VIREMENT' &&
        valeur.compteDestinationId !== null &&
        valeur.compteDestinationId !== undefined
        ? Number(valeur.compteDestinationId)
        : undefined;
    const payload = {
      libelle: valeur.libelle ?? '',
      type: typeReel,
      montant: Number(valeur.montant ?? 0),
      dateOperation: valeur.dateOperation ?? '',
      dateValeur: valeur.dateValeur || null,
      estPointee: valeur.estPointee ?? false,
      operationPlanifieeId:
        valeur.operationPlanifieeId !== null && valeur.operationPlanifieeId !== undefined
          ? Number(valeur.operationPlanifieeId)
          : undefined,
      categorie:
        valeur.categorie && valeur.categorie.trim().length > 0
          ? valeur.categorie.trim()
          : undefined,
      moyenPaiement: moyenPaiement ?? undefined,
      numeroCheque,
      compteDestinationId,
    };

    const edition = this.operationReelleEditee();

    if (edition) {
      void this.comptesService
        .mettreAJourOperationReelle(edition.id, payload)
        .subscribe({
          next: () => {
            this.annulerEditionOperationReelle();
            void this.rechargerPeriodeCourante();
            this.actualiserCompteSelectionne();
            this.afficherSucces('Operation reelle mise a jour.');
          },
          error: () =>
            this.signalerErreur("Impossible de mettre a jour l'operation reelle."),
        });
      return;
    }

    void this.comptesService
      .creerOperationReelle(compte.id, payload)
      .subscribe({
        next: () => {
          this.annulerEditionOperationReelle();
          void this.rechargerPeriodeCourante();
          this.actualiserCompteSelectionne();
          this.afficherSucces('Operation reelle enregistree.');
        },
        error: () => this.signalerErreur("Impossible d'enregistrer l'operation reelle."),
      });
  }
  pointerOperation(operationId: number): void {
    const compte = this.compteSelectionne();
    if (!compte) {
      return;
    }

    void this.comptesService.pointerOperationReelle(operationId).subscribe({
      next: () => {
        void this.rechargerPeriodeCourante();
        this.actualiserCompteSelectionne();
        this.afficherSucces('Operation pointee.');
      },
      error: () => this.signalerErreur("Impossible de pointer l'operation."),
    });
  }

  supprimerOperationReelle(operationId: number): void {
    const compte = this.compteSelectionne();
    if (!compte) {
      return;
    }

    if (this.operationReelleEditee()?.id === operationId) {
      this.annulerEditionOperationReelle();
    }

    void this.comptesService.supprimerOperationReelle(operationId).subscribe({
      next: () => {
        void this.rechargerPeriodeCourante();
        this.actualiserCompteSelectionne();
        this.afficherSucces('Operation reelle supprimee.');
      },
      error: () => this.signalerErreur('Suppression impossible.'),
    });
  }

  mettreAJourSoldeActuel(): void {
    const compte = this.compteSelectionne();
    if (!compte) {
      return;
    }

    if (this.soldeActuelForm.invalid) {
      this.soldeActuelForm.markAllAsTouched();
      return;
    }

    const valeur = this.soldeActuelForm.getRawValue();
    const soldeActuel = Number(valeur.soldeActuel);

    if (Number.isNaN(soldeActuel)) {
      this.soldeActuelForm.controls.soldeActuel.setErrors({ format: true });
      return;
    }

    void this.comptesService
      .mettreAJourSoldeCompte(compte.id, soldeActuel)
      .subscribe({
        next: (compteMisAJour) => {
          this.soldesPlanifiesMemo.clear();
          this.periodeMemoMax = null;
          this.soldeBaseCourant = 0;
          this.mettreAJourSoldeActuelForm(compteMisAJour);
          this.mettreAJourSoldeCBDiffereForm(compteMisAJour);
          this.compteSelectionne.set(compteMisAJour);
          void this.rechargerPeriodeCourante();
          this.actualiserCompteSelectionne();
          this.afficherSucces('Solde actuel mis a jour.');
        },
        error: () => this.signalerErreur('Impossible de mettre a jour le solde.'),
      });
  }

  mettreAJourSoldeCBDiffere(): void {
    const compte = this.compteSelectionne();
    if (!compte) {
      return;
    }

    if (this.soldeCBDiffereForm.invalid) {
      this.soldeCBDiffereForm.markAllAsTouched();
      return;
    }

    const valeur = this.soldeCBDiffereForm.getRawValue();
    const soldeCBDiffere = Number(valeur.soldeCBDiffere);

    if (Number.isNaN(soldeCBDiffere)) {
      this.soldeCBDiffereForm.controls.soldeCBDiffere.setErrors({ format: true });
      return;
    }

    void this.comptesService
      .mettreAJourSoldeCBDiffere(compte.id, soldeCBDiffere)
      .subscribe({
        next: (compteMisAJour) => {
          this.mettreAJourSoldeCBDiffereForm(compteMisAJour);
          this.compteSelectionne.set(compteMisAJour);
          this.actualiserCompteSelectionne();
          this.afficherSucces('Solde CB différé mis à jour.');
        },
        error: () =>
          this.signalerErreur('Impossible de mettre à jour le solde CB différé.'),
      });
  }

  private mettreAJourSoldeActuelForm(compte: Compte): void {
    const valeur =
      compte.soldeActuel !== undefined && compte.soldeActuel !== null
        ? compte.soldeActuel
        : compte.soldeInitial;

    const brut = Number(valeur ?? 0);
    const normalise = Number.isFinite(brut)
      ? Number(brut.toFixed(2))
      : 0;

    this.soldeActuelForm.enable();
    this.soldeActuelForm.setValue({ soldeActuel: normalise });
    this.soldeActuelForm.markAsPristine();
  }

  private mettreAJourSoldeCBDiffereForm(compte: Compte): void {
    const valeur =
      compte.soldeCBDiffere !== undefined && compte.soldeCBDiffere !== null
        ? compte.soldeCBDiffere
        : 0;

    const brut = Number(valeur ?? 0);
    const normalise = Number.isFinite(brut)
      ? Number(brut.toFixed(2))
      : 0;

    this.soldeCBDiffereForm.enable();
    this.soldeCBDiffereForm.setValue({ soldeCBDiffere: normalise });
    this.soldeCBDiffereForm.markAsPristine();
  }

  private reinitialiserFormOperationPlanifiee(): void {
    this.operationPlanifieeForm.reset({
      libelle: '',
      type: 'SORTIE',
      montant: 0,
      dateDebut: '',
      nombreDeMois: null,
      jourDuMois: 1,
      estEtalee: false,
      provision: false,
      valeurRestante: 0,
      moyenPaiement: null,
      numeroCheque: '',
      compteDestinationId: null,
      nombreMensualites: null,
      montantInitial: 0,
      categorie: '',
    });
    this.operationPlanifieeEditee.set(null);
    this.operationPlanifieeFormOuverte.set(false);
    this.mettreAJourChampsMoyenPaiementPlanifie(this.operationPlanifieeForm.get('moyenPaiement')?.value);
    this.mettreAJourChampsEtalement(false);
  }

  montantPlanifiePourOperation(
    operation: OperationPlanifiee,
    annee: number,
    mois: number
  ): number {
    if (operation.provision) {
      const anneeCourante = this.aujourdHui.getFullYear();
      const moisCourant = this.aujourdHui.getMonth() + 1;

      // Pour les mois suivants (futur), on affiche le montant initial (enveloppe complète)
      if (annee > anneeCourante || (annee === anneeCourante && mois > moisCourant)) {
        return Number(operation.montant);
      }

      // Pour les mois passes, on retourne 0 (plus de provision active)
      if (annee < anneeCourante || (annee === anneeCourante && mois < moisCourant)) {
        return 0;
      }

      const restante =
        operation.valeurRestante !== null && operation.valeurRestante !== undefined
          ? operation.valeurRestante
          : operation.montant;
      return Number.isFinite(restante) ? Number(restante) : 0;
    }

    const dateDebut = new Date(operation.dateDebut);
    const diff =
      (annee - dateDebut.getFullYear()) * 12 +
      (mois - (dateDebut.getMonth() + 1));

    if (diff < 0) {
      return 0;
    }

    if (
      operation.nombreDeMois &&
      operation.nombreDeMois > 0 &&
      diff >= operation.nombreDeMois
    ) {
      return 0;
    }

    const montant = Number(operation.montant);

    if (operation.estEtalee && operation.nombreDeMois && operation.nombreDeMois > 0) {
      return montant / operation.nombreDeMois;
    }

    return montant;
  }

  montantRestantPlanifie(
    detail: PrevisionMensuelle | null,
    type: TypeOperation
  ): number {
    if (!detail) {
      return 0;
    }

    const operationsPlanifiees = this.operationsPlanifiees();
    const operationsReelles = this.operationsReelles();
    const periode = this.periode();

    const totalRestant = operationsPlanifiees.reduce((somme, operation) => {
      if (operation.type !== type) {
        return somme;
      }

      const montantPlanifie = this.montantPlanifiePourOperation(
        operation,
        periode.annee,
        periode.mois
      );

      if (montantPlanifie <= 0) {
        return somme;
      }

      if (operation.provision) {
        // montantPlanifie = valeurRestante (déjà mis à jour après chaque utilisation)
        return somme + montantPlanifie;
      }

      const montantRealise = operationsReelles
        .filter((reelle) => reelle.operationPlanifieeId === operation.id)
        .reduce((acc, reelle) => acc + reelle.montant, 0);

      const restant = montantPlanifie - montantRealise;

      if (restant <= 0) {
        return somme;
      }

      return somme + restant;
    }, 0);

    return Number(totalRestant.toFixed(2));
  }

  totalRestantPlanifie(detail: PrevisionMensuelle | null): number {
    if (!detail) {
      return 0;
    }

    const restantEntrees = this.montantRestantPlanifie(detail, 'ENTREE');
    const restantSorties = this.montantRestantPlanifie(detail, 'SORTIE');

    return Number((restantEntrees - restantSorties).toFixed(2));
  }

  totalPlanifie(detail: PrevisionMensuelle | null): number {
    if (!detail) {
      return 0;
    }

    const restantEntrees = detail.planifie.entrees;
    const restantSorties = detail.planifie.sorties;

    return Number((restantEntrees - restantSorties).toFixed(2));
  }

  soldePlanifieFinDeMois(detail: PrevisionMensuelle | null): number {
    if (!detail) {
      return 0;
    }

    const compte = this.compteSelectionne();
    const periode = this.periode();
    const aujourdHui = this.aujourdHui;
    const estPeriodeCourante =
      periode.annee === aujourdHui.getFullYear() &&
      periode.mois === aujourdHui.getMonth() + 1;

    const soldeBase = estPeriodeCourante && compte && compte.soldeActuel !== undefined && compte.soldeActuel !== null
      ? Number(compte.soldeActuel)
      : Number.isFinite(this.soldeBaseCourant)
        ? this.soldeBaseCourant
        : compte && compte.soldeInitial !== undefined && compte.soldeInitial !== null
          ? Number(compte.soldeInitial)
          : Number(detail.compte.soldeInitial ?? 0);

    const soldeCBDiffere = compte && compte.soldeCBDiffere !== undefined && compte.soldeCBDiffere !== null
      ? Number(compte.soldeCBDiffere)
      : 0;

    let soldeBaseAjuste = Number((soldeBase + soldeCBDiffere).toFixed(2));
    if (!estPeriodeCourante) {
      soldeBaseAjuste = soldeBase;
    }

    // Le solde de fin de mois est le solde de base + le budget net du mois (reste planifié + tout le réel)
    // Attention: soldeBase (si soldeActuel) inclut déjà le réel pointé.
    // Donc si on part de soldeActuel, on ne doit ajouter que le (reste planifié + réel NON pointé).

    // Si on est sur un mois futur (soldeBase calculé), il n'inclut rien du mois?
    // SoldeBaseCourant est le solde calculé fin du mois M-1.

    // Simplification: le calcul actuel "netRestant" dans soldePlanifieFinDeMois faisait:
    // totalRestantPlanifie + totalRestantReelle (qui est en fait le réel NON pointé selon totalRestantReelle qui appelle montantRestantReelle).

    // Donc la formule actuelle de soldePlanifieFinDeMois semble correcte pour la projection de fin de mois.

    const netRestant = this.totalRestantPlanifie(detail) + this.totalRestantReelle(detail);

    return Number((soldeBaseAjuste + netRestant).toFixed(2));
  }

  soldeBudgetNet(detail: PrevisionMensuelle | null): number {
    if (!detail) {
      return 0;
    }
    // Budget Net = Planifié Restant + Réel (Pointé + Non Pointé)
    // C'est le résultat net prévisionnel du mois
    const planifieRestant = this.totalRestantPlanifie(detail);
    const reelRestant = this.totalRestantReelle(detail);

    return Number((planifieRestant + reelRestant).toFixed(2));
  }

  totalRestantReelle(detail: PrevisionMensuelle) {
    if (!detail) {
      return 0;
    }
    const restantEntrees = this.montantRestantReelle(detail, 'ENTREE');
    const restantSorties = this.montantRestantReelle(detail, 'SORTIE');
    return Number((restantEntrees + restantSorties).toFixed(2));
  }

  montantRestantReelle(detail: PrevisionMensuelle, typeOperation: string) {
    if (!detail) {
      return 0;
    }
    const type = typeOperation as TypeOperation;
    const operationsReelles = this.operationsReelles();
    const periode = this.periode();
    // Le reste d'une provision couvre déjà les opérations liées en attente.
    // Seule la part éventuelle qui dépasse ce reste s'ajoute au prévisionnel.
    const couvertureProvisions = new Map(this.operationsPlanifiees()
      .filter((op) => op.provision && op.type === type)
      .map((op) => [op.id, Math.max(0, this.montantPlanifiePourOperation(op, periode.annee, periode.mois))]));
    const totalRestant = operationsReelles.reduce((somme, operation) => {
      if (operation.estPointee) {
        return somme;
      }

      if (operation.type !== type) {
        return somme;
      }

      const montant = Number(operation.montant);
      if (!Number.isFinite(montant)) {
        return somme;
      }

      const provisionId = operation.operationPlanifieeId ?? -1;
      const couverture = couvertureProvisions.get(provisionId) ?? 0;
      const montantCouvert = Math.min(montant, couverture);
      couvertureProvisions.set(provisionId, couverture - montantCouvert);
      const montantSupplementaire = montant - montantCouvert;
      return somme + (operation.type === 'ENTREE' ? montantSupplementaire : -montantSupplementaire);
    }, 0);

    return Number(totalRestant.toFixed(2));
  }

  private clePeriode(annee: number, mois: number): string {
    return `${annee}-${mois.toString().padStart(2, '0')}`;
  }

  private periodePrecedentePourCalcul(annee: number, mois: number): {
    annee: number;
    mois: number;
  } {
    const date = new Date(annee, mois - 1, 1);
    date.setMonth(date.getMonth() - 1);
    return {
      annee: date.getFullYear(),
      mois: date.getMonth() + 1,
    };
  }

  private calculerSoldeBasePourPeriode(
    annee: number,
    mois: number,
    compte: Compte | null,
    soldeInitial: number
  ): number {
    const periodePrec = this.periodePrecedentePourCalcul(annee, mois);
    const clePrecedente = this.clePeriode(periodePrec.annee, periodePrec.mois);
    const soldePrecedent = this.soldesPlanifiesMemo.get(clePrecedente);

    if (soldePrecedent !== undefined) {
      return soldePrecedent;
    }

    if (compte) {
      if (compte.soldeActuel !== undefined && compte.soldeActuel !== null) {
        return Number(compte.soldeActuel);
      }
      return Number(compte.soldeInitial);
    }

    return Number(soldeInitial);
  }


  private estPeriodeUlterieureOuEgale(
    periodeA: { annee: number; mois: number },
    periodeB: { annee: number; mois: number }
  ): boolean {
    const indexA = periodeA.annee * 12 + (periodeA.mois - 1);
    const indexB = periodeB.annee * 12 + (periodeB.mois - 1);
    return indexA >= indexB;
  }

  libelleOperationPlanifieePourOperation(operation: OperationReelle | null | undefined): string {
    if (!operation) {
      return 'Aucune';
    }

    if (operation.operationPlanifieeLibelle !== undefined && operation.operationPlanifieeLibelle !== null) {
      return operation.operationPlanifieeLibelle;
    }

    const id = operation.operationPlanifieeId;

    if (id === null || id === undefined) {
      return 'Aucune';
    }

    const correspondance = this.operationsPlanifiees().find(
      (planifiee) => planifiee.id === id
    );

    if (correspondance) {
      return correspondance.libelle;
    }

    return 'Aucune';
  }

  comptesDestinationDisponibles(): Compte[] {
    const compteActuel = this.compteSelectionne();
    return this.comptes().filter((compte) => compteActuel ? compte.id !== compteActuel.id : true);
  }

  private mettreAJourChampsMoyenPaiementPlanifie(moyen: string | null | undefined): void {
    const numeroCtrl = this.operationPlanifieeForm.get('numeroCheque');
    const destinationCtrl = this.operationPlanifieeForm.get('compteDestinationId');
    if (!numeroCtrl || !destinationCtrl) {
      return;
    }

    const estCheque = moyen === 'CHEQUE';
    const estVirement = moyen === 'VIREMENT';

    if (estCheque) {
      numeroCtrl.enable({ emitEvent: false });
      numeroCtrl.setValidators([Validators.required]);
    } else {
      numeroCtrl.reset('', { emitEvent: false });
      numeroCtrl.clearValidators();
      numeroCtrl.disable({ emitEvent: false });
    }
    numeroCtrl.updateValueAndValidity({ emitEvent: false });

    if (estVirement) {
      destinationCtrl.enable({ emitEvent: false });
      destinationCtrl.setValidators([Validators.required]);
    } else {
      destinationCtrl.reset(null, { emitEvent: false });
      destinationCtrl.clearValidators();
      destinationCtrl.disable({ emitEvent: false });
    }
    destinationCtrl.updateValueAndValidity({ emitEvent: false });
  }

  private mettreAJourChampsMoyenPaiementPointage(moyen: unknown): void {
    const numeroCtrl = this.pointerOperationPlanifieeForm.get('numeroCheque');
    const destinationCtrl = this.pointerOperationPlanifieeForm.get('compteDestinationId');
    if (!numeroCtrl || !destinationCtrl) {
      return;
    }

    const estCheque = moyen === 'CHEQUE';
    const estVirement = moyen === 'VIREMENT';

    if (estCheque) {
      numeroCtrl.enable({ emitEvent: false });
      numeroCtrl.setValidators([Validators.required]);
    } else {
      numeroCtrl.reset('', { emitEvent: false });
      numeroCtrl.clearValidators();
      numeroCtrl.disable({ emitEvent: false });
    }
    numeroCtrl.updateValueAndValidity({ emitEvent: false });

    if (estVirement) {
      destinationCtrl.enable({ emitEvent: false });
      destinationCtrl.setValidators([Validators.required]);
    } else {
      destinationCtrl.reset(null, { emitEvent: false });
      destinationCtrl.clearValidators();
      destinationCtrl.disable({ emitEvent: false });
    }
    destinationCtrl.updateValueAndValidity({ emitEvent: false });
  }

  private mettreAJourChampsMoyenPaiementReelle(moyen: string | null | undefined): void {
    const numeroCtrl = this.operationReelleForm.get('numeroCheque');
    const destinationCtrl = this.operationReelleForm.get('compteDestinationId');
    if (!numeroCtrl || !destinationCtrl) {
      return;
    }

    const estCheque = moyen === 'CHEQUE';
    const estVirement = moyen === 'VIREMENT';

    if (estCheque) {
      numeroCtrl.enable({ emitEvent: false });
      numeroCtrl.setValidators([Validators.required]);
    } else {
      numeroCtrl.reset('', { emitEvent: false });
      numeroCtrl.clearValidators();
      numeroCtrl.disable({ emitEvent: false });
    }
    numeroCtrl.updateValueAndValidity({ emitEvent: false });

    if (estVirement) {
      destinationCtrl.enable({ emitEvent: false });
      destinationCtrl.setValidators([Validators.required]);
    } else {
      destinationCtrl.reset(null, { emitEvent: false });
      destinationCtrl.clearValidators();
      destinationCtrl.disable({ emitEvent: false });
    }
    destinationCtrl.updateValueAndValidity({ emitEvent: false });
  }

  private mettreAJourChampsEtalement(actif: unknown): void {
    const estActif = actif === true;
    this.etalementActif.set(estActif);
    const nbCtrl = this.operationPlanifieeForm.get('nombreMensualites');
    const montantInitialCtrl = this.operationPlanifieeForm.get('montantInitial');
    if (!nbCtrl || !montantInitialCtrl) {
      return;
    }

    if (estActif) {
      nbCtrl.setValidators([Validators.required, Validators.min(2), Validators.max(120)]);
      nbCtrl.enable({ emitEvent: false });
      montantInitialCtrl.setValidators([Validators.required, Validators.min(0.01)]);
      montantInitialCtrl.enable({ emitEvent: false });
    } else {
      nbCtrl.reset(null, { emitEvent: false });
      nbCtrl.clearValidators();
      nbCtrl.disable({ emitEvent: false });
      montantInitialCtrl.reset(0, { emitEvent: false });
      montantInitialCtrl.clearValidators();
      montantInitialCtrl.disable({ emitEvent: false });
    }

    nbCtrl.updateValueAndValidity({ emitEvent: false });
    montantInitialCtrl.updateValueAndValidity({ emitEvent: false });
    this.mettreAJourMontantMensualite();
  }

  private mettreAJourMontantMensualite(): void {
    const actif = this.operationPlanifieeForm.get('estEtalee')?.value;
    if (!actif) {
      return;
    }

    const montantInitial = Number(this.operationPlanifieeForm.get('montantInitial')?.value ?? 0);
    const nbMensualites = Number(this.operationPlanifieeForm.get('nombreMensualites')?.value ?? 0);
    const montantCtrl = this.operationPlanifieeForm.get('montant');

    if (!montantCtrl) {
      return;
    }

    if (montantInitial > 0 && nbMensualites > 0) {
      const part = Math.ceil((montantInitial / nbMensualites) * 100) / 100;
      montantCtrl.setValue(Number(part.toFixed(2)), { emitEvent: false });
    }
  }

  private async creerOperationsMensualisees(
    compte: Compte,
    valeurForm: any,
    basePayload: any,
    options: { nombreMensualites: number; montantInitial: number; dateDebut: string }
  ): Promise<void> {
    const montants = this.calculerMontantsMensualites(options.montantInitial, options.nombreMensualites);
    const dateDepart = options.dateDebut;
    const groupeRef = crypto.randomUUID();

    const numeroChequeBase: string | null =
      basePayload.moyenPaiement === 'CHEQUE' && basePayload.numeroCheque
        ? String(basePayload.numeroCheque).trim()
        : null;

    for (let i = 0; i < options.nombreMensualites; i += 1) {
      const dateMensualite = this.ajouterMois(dateDepart, i);
      const libelleBase = valeurForm.libelle ?? '';

      let numeroCheque = basePayload.numeroCheque;
      if (numeroChequeBase !== null && i > 0) {
        // Incrémenter la partie numérique finale du numéro de chèque
        const match = numeroChequeBase.match(/^(.*?)(\d+)$/);
        if (match) {
          const prefix = match[1];
          const num = parseInt(match[2], 10) + i;
          const padded = String(num).padStart(match[2].length, '0');
          numeroCheque = `${prefix}${padded}`;
        } else {
          numeroCheque = numeroChequeBase;
        }
      }

      const payloadMensualite = {
        ...basePayload,
        libelle: `${libelleBase} (${i + 1}/${options.nombreMensualites})`,
        dateDebut: dateMensualite,
        nombreDeMois: 1,
        jourDuMois: new Date(dateMensualite).getDate(),
        montant: montants[i],
        nombreMensualites: options.nombreMensualites,
        montantInitial: options.montantInitial,
        groupeRef,
        ...(numeroChequeBase !== null ? { numeroCheque } : {}),
      };

      await firstValueFrom(
        this.comptesService.creerOperationPlanifiee(compte.id, payloadMensualite)
      );
    }
  }

  private calculerMontantsMensualites(total: number, nombre: number): number[] {
    const montants: number[] = [];
    const part = Math.ceil((total / nombre) * 100) / 100;
    let restant = Number(total.toFixed(2));

    for (let i = 0; i < nombre; i += 1) {
      if (i === nombre - 1) {
        montants.push(Number(restant.toFixed(2)));
      } else {
        const montant = Number(Math.min(part, restant).toFixed(2));
        montants.push(montant);
        restant = Number((restant - montant).toFixed(2));
      }
    }

    return montants;
  }

  private ajouterMois(dateISO: string, offset: number): string {
    const base = dateISO ? new Date(dateISO) : new Date();
    const cible = new Date(base.getFullYear(), base.getMonth() + offset, base.getDate());
    const annee = cible.getFullYear();
    const mois = String(cible.getMonth() + 1).padStart(2, '0');
    const jour = String(cible.getDate()).padStart(2, '0');
    return `${annee}-${mois}-${jour}`;
  }

  soldeOperationsReelles(estPointees: boolean): number {
    const ciblePointee = estPointees === true;
    return this.operationsReelles().reduce((total, operation) => {
      const estPointeeOperation = operation.estPointee === true;
      if (estPointeeOperation !== ciblePointee) {
        return total;
      }

      const montant = Number(operation.montant);
      if (!Number.isFinite(montant)) {
        return total;
      }

      return total + (operation.type === 'ENTREE' ? montant : -montant);
    }, 0);
  }

  TotalOperationsReelles(estPointees: boolean, estSorties: boolean): number {
    const ciblePointee = estPointees === true;
    const signe = estSorties ? -1 : 1;
    return this.operationsReelles().reduce((total, operation) => {
      const estPointeeOperation = operation.estPointee === true;
      const estSortieOperation = operation.type === 'SORTIE';
      if (estPointeeOperation !== ciblePointee || estSortieOperation !== estSorties) {
        return total;
      }

      const montant = Number(operation.montant);
      if (!Number.isFinite(montant)) {
        return total;
      }

      return total + (operation.type === 'ENTREE' ? montant : -montant);
    }, 0);
  }

  formatterMontant(valeur: number | null | undefined): string {
    if (valeur === null || valeur === undefined || Number.isNaN(valeur)) {
      return `0,00 ${EURO}`;
    }

    const montantFormate = valeur.toLocaleString('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return `${montantFormate} ${EURO}`;
  }

  formatterDate(dateStr: string | null | undefined): string {
    if (!dateStr) return '';
    
    const date = new Date(dateStr);
    const maintenant = new Date();
    const diff = maintenant.getTime() - date.getTime();
    const jours = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (jours === 0) {
      return "Aujourd'hui";
    } else if (jours === 1) {
      return 'Hier';
    } else if (jours < 7) {
      return `Il y a ${jours} jours`;
    } else if (jours < 30) {
      const semaines = Math.floor(jours / 7);
      return `Il y a ${semaines} semaine${semaines > 1 ? 's' : ''}`;
    } else {
      return date.toLocaleDateString('fr-FR', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric' 
      });
    }
  }

  private signalerErreur(message: string): void {
    this.erreurs.set(message);
    setTimeout(() => this.erreurs.set(null), 4000);
  }

  private afficherSucces(message: string): void {
    this.succes.set(message);
    setTimeout(() => this.succes.set(null), 3000);
  }

  private actualiserCompteSelectionne(): void {
    const compteActuel = this.compteSelectionne();
    if (!compteActuel) {
      return;
    }
    void this.comptesService.rafraichirComptes().subscribe((liste) => {
      const miseAJour = liste.find((c) => c.id === compteActuel.id);
      if (miseAJour) {
        this.compteSelectionne.set(miseAJour);
        this.mettreAJourSoldeActuelForm(miseAJour);
        this.mettreAJourSoldeCBDiffereForm(miseAJour);
      } else {
        this.soldeActuelForm.disable();
        this.soldeCBDiffereForm.disable();
        this.annulerPointageOperationPlanifiee();
      }
    });
  }

  // Méthodes pour tri et filtrage des opérations planifiées
  changerTriPlanifiee(colonne: 'libelle' | 'montant' | 'jour' | 'categorie'): void {
    if (this.triPlanifieeColonne() === colonne) {
      // Inverser l'ordre si on clique sur la même colonne
      this.triPlanifieeOrdre.set(this.triPlanifieeOrdre() === 'asc' ? 'desc' : 'asc');
    } else {
      this.triPlanifieeColonne.set(colonne);
      this.triPlanifieeOrdre.set('asc');
    }
  }

  reinitialiserFiltresPlanifiees(): void {
    this.filtreLibelleTexte.set('');
    this.filtreMontantMin.set(null);
    this.filtreMontantMax.set(null);
    this.filtreMontantType.set('TOUS');
    this.filtreJour.set(null);
    this.filtreCategorieTexte.set('');
    this.filtreCategorieIds.set([]);
    this.filtreProvision.set('TOUS');
    this.triPlanifieeColonne.set(null);
  }

  basculerDropdownColonne(colonne: 'libelle' | 'montant' | 'jour' | 'categorie'): void {
    if (this.colonneDropdownOuverte() === colonne) {
      this.colonneDropdownOuverte.set(null);
    } else {
      this.colonneDropdownOuverte.set(colonne);
    }
  }

  fermerTousLesDropdowns(): void {
    this.colonneDropdownOuverte.set(null);
  }

  reinitialiserFiltreColonne(colonne: 'libelle' | 'montant' | 'jour' | 'categorie'): void {
    switch (colonne) {
      case 'libelle':
        this.filtreLibelleTexte.set('');
        break;
      case 'montant':
        this.filtreMontantMin.set(null);
        this.filtreMontantMax.set(null);
        this.filtreMontantType.set('TOUS');
        break;
      case 'jour':
        this.filtreJour.set(null);
        break;
      case 'categorie':
        this.filtreCategorieTexte.set('');
        this.filtreCategorieIds.set([]);
        break;
    }
  }

  basculerCategorieFiltre(categorieId: number): void {
    const ids = this.filtreCategorieIds();
    if (ids.includes(categorieId)) {
      this.filtreCategorieIds.set(ids.filter(id => id !== categorieId));
    } else {
      this.filtreCategorieIds.set([...ids, categorieId]);
    }
  }

  parseNumber(value: string): number | null {
    const num = Number(value);
    return isNaN(num) || value === '' ? null : num;
  }

  // Méthodes pour tri et filtrage des opérations réelles
  changerTriReelle(colonne: 'libelle' | 'montant' | 'date' | 'statut'): void {
    if (this.triReelleColonne() === colonne) {
      this.triReelleOrdre.set(this.triReelleOrdre() === 'asc' ? 'desc' : 'asc');
    } else {
      this.triReelleColonne.set(colonne);
      this.triReelleOrdre.set('asc');
    }
  }

  reinitialiserFiltresReelles(): void {
    this.filtreReelleLibelleTexte.set('');
    this.filtreReelleMontantMin.set(null);
    this.filtreReelleMontantMax.set(null);
    this.filtreReelleMontantType.set('TOUS');
    this.filtreReelleDate.set('');
    this.filtreReelleStatut.set('TOUS');
    this.triReelleColonne.set(null);
  }

  basculerDropdownReelleColonne(colonne: 'libelle' | 'montant' | 'date' | 'statut'): void {
    if (this.colonneReelleDropdownOuverte() === colonne) {
      this.colonneReelleDropdownOuverte.set(null);
    } else {
      this.colonneReelleDropdownOuverte.set(colonne);
    }
  }

  fermerTousLesDropdownsReelles(): void {
    this.colonneReelleDropdownOuverte.set(null);
  }

  reinitialiserFiltreReelleColonne(colonne: 'libelle' | 'montant' | 'date' | 'statut'): void {
    switch (colonne) {
      case 'libelle':
        this.filtreReelleLibelleTexte.set('');
        break;
      case 'montant':
        this.filtreReelleMontantMin.set(null);
        this.filtreReelleMontantMax.set(null);
        this.filtreReelleMontantType.set('TOUS');
        break;
      case 'date':
        this.filtreReelleDate.set('');
        break;
      case 'statut':
        this.filtreReelleStatut.set('TOUS');
        break;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    // Vérifie si le clic est sur un bouton filtre ou dans un dropdown
    const isClickOnFilterButton = target.closest('.btn-filtre-colonne');
    const isClickInDropdown = target.closest('.dropdown-filtre');

    // Ferme les dropdowns seulement si le clic est en dehors des boutons et des dropdowns
    if (!isClickOnFilterButton && !isClickInDropdown) {
      this.fermerTousLesDropdowns();
      this.fermerTousLesDropdownsReelles();
    }
  }

  rechargerPeriodeCourante(): void {
    const compte = this.compteSelectionne();
    if (!compte) {
      return;
    }

    const periode = this.periode();
    void this.chargerOperationsPlanifiees(
      compte.id,
      periode.annee,
      periode.mois
    );
    void this.chargerOperationsReelles(compte.id, periode.annee, periode.mois);
    void this.chargerPrevision(compte.id, periode.annee, periode.mois);
    this.actualiserCompteSelectionne();
  }

  private chargerCategories(): void {
    void this.comptesService.listerCategories().subscribe({
      next: (liste) => this.categories.set(liste),
      error: () => this.signalerErreur('Impossible de charger les categories.'),
    });
  }

  ajouterCategorie(): void {
    const saisie = prompt('Nom de la nouvelle catégorie ?');
    const normalise = saisie ? saisie.trim() : '';
    if (!normalise) {
      return;
    }

    void this.comptesService.creerCategorie(normalise).subscribe({
      next: (categorie) => {
        const maj = [...this.categories(), categorie].sort((a, b) =>
          a.nom.localeCompare(b.nom, 'fr', { sensitivity: 'base' })
        );
        this.categories.set(maj);
        this.afficherSucces('Catégorie ajoutée.');
      },
      error: (erreur) => {
        const message =
          (erreur?.error?.message as string) ??
          "Impossible d'ajouter la catégorie.";
        this.signalerErreur(message);
      },
    });
  }
}
